import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Spinner } from "@/components/ui/feedback/Spinner";
import type { ButtonProps, IconButtonProps } from "./Button.types";

const buttonVariants = cva(
  "bfrs-inline-flex bfrs-min-w-0 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-[var(--bfrs-button-radius)] bfrs-outline-none bfrs-transition bfrs-duration-150 bfrs-ease-out focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-pointer-events-none disabled:bfrs-opacity-55",
  {
    variants: {
      variant: {
        primary: "bfrs-bg-primary bfrs-font-bold bfrs-text-primary-contrast hover:bfrs-bg-primary-hover active:bfrs-bg-primary-active",
        secondary: "bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-font-normal bfrs-text-text-primary hover:bfrs-bg-primary-soft active:bfrs-bg-surface-muted",
        tertiary: "bfrs-h-auto bfrs-rounded-sm bfrs-border bfrs-border-transparent bfrs-bg-transparent bfrs-px-0 bfrs-py-0 bfrs-font-semibold bfrs-text-primary bfrs-underline bfrs-underline-offset-4 hover:bfrs-bg-primary-soft hover:bfrs-text-primary-hover active:bfrs-bg-accent-soft",
        outline: "bfrs-border bfrs-border-primary bfrs-bg-primary-soft bfrs-font-semibold bfrs-text-primary hover:bfrs-bg-accent-soft active:bfrs-bg-surface-raised",
        ghost: "bfrs-bg-transparent bfrs-text-text-secondary hover:bfrs-bg-primary-soft hover:bfrs-text-text-primary active:bfrs-bg-surface-muted",
        destructive: "bfrs-border bfrs-border-danger bfrs-bg-surface-card bfrs-font-semibold bfrs-text-danger hover:bfrs-bg-danger-soft active:bfrs-bg-danger-soft",
        danger: "bfrs-border bfrs-border-danger bfrs-bg-surface-card bfrs-font-semibold bfrs-text-danger hover:bfrs-bg-danger-soft active:bfrs-bg-danger-soft",
        link: "bfrs-h-auto bfrs-rounded-sm bfrs-border bfrs-border-transparent bfrs-bg-transparent bfrs-p-0 bfrs-font-semibold bfrs-text-primary bfrs-underline-offset-4 hover:bfrs-bg-primary-soft hover:bfrs-underline active:bfrs-bg-accent-soft"
      },
      size: {
        xs: "bfrs-h-[26px] bfrs-gap-1.5 bfrs-px-3 bfrs-text-[12px] bfrs-leading-[normal]",
        sm: "bfrs-h-7 bfrs-gap-1.5 bfrs-px-3 bfrs-text-[12px]",
        md: "bfrs-h-[30px] bfrs-gap-2 bfrs-px-[18px] bfrs-text-[12px]",
        lg: "bfrs-h-10 bfrs-gap-2.5 bfrs-px-5 bfrs-text-sm"
      },
      iconOnly: {
        true: "bfrs-aspect-square bfrs-px-0"
      },
      fullWidth: {
        true: "bfrs-w-full"
      }
    },
    compoundVariants: [
      { variant: "tertiary", className: "bfrs-h-auto bfrs-px-0 bfrs-py-0" },
      { variant: "link", className: "bfrs-h-auto bfrs-px-0 bfrs-py-0" },
      { size: "xs", iconOnly: true, className: "bfrs-h-[26px] bfrs-w-[26px] bfrs-px-0" },
      { size: "sm", iconOnly: true, className: "bfrs-h-7 bfrs-w-7 bfrs-px-0" },
      { size: "md", iconOnly: true, className: "bfrs-h-8 bfrs-w-8 bfrs-px-0" },
      { size: "lg", iconOnly: true, className: "bfrs-w-10" }
    ],
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);

const spinnerSize = {
  xs: "sm",
  sm: "sm",
  md: "sm",
  lg: "md"
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      fullWidth,
      iconOnly,
      leftIcon,
      rightIcon,
      loading,
      loadingLabel = "Loading",
      disabled,
      children,
      type = "button",
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={iconOnly && typeof children === "string" ? children : props["aria-label"]}
      className={cn(buttonVariants({ variant, size, fullWidth, iconOnly }), className)}
      {...props}
    >
      {loading ? <Spinner size={spinnerSize[size]} label={loadingLabel} /> : leftIcon}
      {!iconOnly && children ? <span className="bfrs-truncate">{children}</span> : children}
      {!loading && rightIcon}
    </button>
  )
);

Button.displayName = "Button";

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({ icon, size = "md", variant = "ghost", ...props }, ref) => (
  <Button ref={ref} size={size} variant={variant} iconOnly {...props}>
    {props.loading ? null : icon}
  </Button>
));

IconButton.displayName = "IconButton";
